/**
 * #1662: the Snowflake errors a reader can act on from the code alone, as a closed hint. A dbt
 * `WAREHOUSE_ERROR` cause carries only Snowflake's code and SQLSTATE, so its sentence printed
 * numbers a person had to look up. This table gives a known code its own words and fix.
 *
 * Fixed sentences only (ADR 0011): a kind is chosen by the code or the SQLSTATE, never by the
 * message, and no word of the message is ever read here. The runner's driver sentences
 * (`DRIVER_ERROR_SENTENCES` in `runner/steps/sanitiseError.ts`, twin `driver_text.json`) map the
 * same codes to a kind of the same name, which `warehouseErrorKinds.test.ts` holds.
 */

export const WAREHOUSE_ERROR_KINDS = ["division_by_zero"] as const;
export type WarehouseErrorKind = (typeof WAREHOUSE_ERROR_KINDS)[number];

export interface WarehouseErrorHint {
  /** Snowflake's six-digit error codes for the kind. */
  readonly codes: readonly string[];
  /** The SQLSTATEs for the kind. */
  readonly sqlStates: readonly string[];
  /** What happened, a lower-case phrase the cause sentence ends with. */
  readonly words: string;
  /** What a person can do, full sentences. */
  readonly fix: string;
}

export const WAREHOUSE_ERROR_HINTS: Readonly<Record<WarehouseErrorKind, WarehouseErrorHint>> = {
  division_by_zero: {
    codes: ["100051"],
    sqlStates: ["22012"],
    words: "division by zero",
    fix: "Look for a division in the model by a value that can be 0. Use NULLIF or DIV0 to guard it. A contributor can then run dbt again.",
  },
};

/** The kind a Snowflake code, else its SQLSTATE, names, or null when the table has none. */
export function warehouseErrorKind(
  code: string | null | undefined,
  sqlState: string | null | undefined,
): WarehouseErrorKind | null {
  for (const kind of WAREHOUSE_ERROR_KINDS) {
    if (code && WAREHOUSE_ERROR_HINTS[kind].codes.includes(code)) return kind;
  }
  for (const kind of WAREHOUSE_ERROR_KINDS) {
    if (sqlState && WAREHOUSE_ERROR_HINTS[kind].sqlStates.includes(sqlState)) return kind;
  }
  return null;
}
